import { Dispatch } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRichMedia } from 'modules/auth/services/BenguideService';
import * as BenGuideService from 'modules/home/services/BenGuideService';
import { SectionName } from 'modules/home/constants';

type LegalDisclaimerState = {
  content: string;
  defaultContent: string;
  inProgress: boolean;
  error: any;
};

const initialState: LegalDisclaimerState = {
  content: '',
  defaultContent: '',
  inProgress: false,
  error: null,
};

const legalDisclaimerSlice = createSlice({
  name: 'legalDisclaimer',
  initialState,
  reducers: {
    getCurrentRichMediaStarted: (state) => {
      state.inProgress = true;
      state.error = null;
    },
    getCurrentRichMediaSuccess: (state, action: PayloadAction<string>) => {
      state.inProgress = false;
      state.content = action.payload;
      state.error = null;
    },
    getCurrentRichMediaFailed: (state, action: PayloadAction<any>) => {
      state.inProgress = false;
      state.error = action.payload;
    },
    getDefaultRichMediaStarted: (state) => {
      state.inProgress = true;
      state.defaultContent = '';
      state.error = null;
    },
    getDefaultRichMediaSuccess: (state, action: PayloadAction<string>) => {
      state.inProgress = false;
      state.defaultContent = action.payload;
      state.error = null;
    },
    getDefaultRichMediaFailed: (state, action: PayloadAction<any>) => {
      state.inProgress = false;
      state.defaultContent = '';
      state.error = action.payload;
    },
    resetLegalDisclaimerContent: (state) => {
      state.content = '';
      state.error = null;
    },
    createRichMediaStarted: (state) => {
      state.inProgress = true;
      state.error = null;
    },
    createRichMediaSuccess: (state, action: PayloadAction<string>) => {
      state.inProgress = false;
      state.content = action.payload;
      state.error = null;
    },
    createRichMediaFailed: (state, action: PayloadAction<any>) => {
      state.inProgress = false;
      state.error = action.payload;
    },
  },
});

export const {
  getCurrentRichMediaStarted,
  getCurrentRichMediaSuccess,
  getCurrentRichMediaFailed,
  getDefaultRichMediaStarted,
  getDefaultRichMediaSuccess,
  getDefaultRichMediaFailed,
  resetLegalDisclaimerContent,
  createRichMediaStarted,
  createRichMediaSuccess,
  createRichMediaFailed,
} = legalDisclaimerSlice.actions;

export default legalDisclaimerSlice.reducer;

export const getCurrentRichMedia =
  (benguideId: string, sectionName: SectionName) =>
  async (dispatch: Dispatch) => {
    dispatch(getCurrentRichMediaStarted());
    try {
      const response = await getRichMedia(benguideId, sectionName);
      dispatch(getCurrentRichMediaSuccess(response.data.content || ''));
    } catch (error) {
      dispatch(getCurrentRichMediaFailed(error));
    }
  };

export const getDefaultRichMedia =
  (benguideId: string, sectionName: SectionName) =>
  async (dispatch: Dispatch) => {
    dispatch(getDefaultRichMediaStarted());
    try {
      const response = await BenGuideService.getDefaultRichMedia(
        benguideId,
        sectionName
      );
      dispatch(getDefaultRichMediaSuccess(response.data.content || ''));
    } catch (error) {
      dispatch(getDefaultRichMediaFailed(error));
    }
  };

export const createRichMedia =
  (
    benguideId: string,
    sectionName: SectionName,
    content: string,
    onCreate?: Function
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(createRichMediaStarted());
    try {
      const data = {
        sectionName: sectionName,
        content: content,
      };
      await BenGuideService.createRichMedia(benguideId, data);
      dispatch(createRichMediaSuccess(content));
      onCreate && onCreate();
    } catch (error) {
      dispatch(createRichMediaFailed(error));
    }
  };
